import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  FiZap,
  FiCopy,
  FiDownload,
  FiX,
  FiChevronDown,
  FiCheck,
  FiAlertCircle,
  FiCode,
  FiEdit3,
  FiFileText,
  FiFile,
  FiHash,
  FiSmile,
} from 'react-icons/fi';
import { contentService, promptService } from '../services/apiService';
import { useAuthStore, useInputStore } from '../utils/store';
import { SegmentControl } from '../components/Common/GenPage';
import { ToneSelector } from '../components/Common/ToneSelector';
import { OutputToolbar } from '../components/Common/OutputToolbar';
import { useT } from '../utils/i18n';
import { DEFAULT_TONE_BY_CONTENT_TYPE } from '../constants/tones';

// ─── Tab definitions ─────────────────────────────────────────────────────────

const TABS = [
  { id: 'socialMediaAll',       labelKey: 'socialMedia.tab.all' },
  { id: 'socialMediaYouTube',   labelKey: 'socialMedia.tab.youtube' },
  { id: 'socialMediaFacebook',  labelKey: 'socialMedia.tab.facebook' },
  { id: 'socialMediaInstagram', labelKey: 'socialMedia.tab.instagram' },
  { id: 'socialMediaTwitter',   labelKey: 'socialMedia.tab.twitter' },
];

// ─── Component ───────────────────────────────────────────────────────────────

export const SocialMedia = () => {
  const t = useT();
  const { user } = useAuthStore();
  const { globalInputText, setGlobalInputText, clearGlobalInputText } = useInputStore();
  const navigate  = useNavigate();

  const [activeTab, setActiveTab]       = useState('socialMediaAll');
  const input = globalInputText;
  const setInput = setGlobalInputText;
  const [output, setOutput]             = useState('');
  const [tone, setTone]                 = useState(DEFAULT_TONE_BY_CONTENT_TYPE['socialMediaAll'] || 'authoritative');
  const [hashtags, setHashtags]         = useState('Include');
  const [emojis, setEmojis]             = useState('Include');
  const [postCount, setPostCount]       = useState('3');
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState('');
  const [copySuccess, setCopySuccess]   = useState(false);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [promptOpen, setPromptOpen]     = useState(false);
  const [promptData, setPromptData]     = useState(null);
  const [promptLoading, setPromptLoading] = useState(false);

  // Output toolbar — font controls, edit/feedback-loop state
  const [fontSize, setFontSize]         = useState(16);
  const [fontFamily, setFontFamily]     = useState('Noto Sans Telugu');
  const [isEditing, setIsEditing]       = useState(false);
  const [editedText, setEditedText]     = useState('');
  const [originalAiText, setOriginalAiText] = useState('');
  const [savedEditText, setSavedEditText]   = useState('');
  const [hasUnsavedEdit, setHasUnsavedEdit] = useState(false);

  const outputRef   = useRef(null);
  const downloadRef = useRef(null);
  const editableRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (downloadRef.current && !downloadRef.current.contains(e.target)) {
        setDownloadOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleTabChange = (tabId) => {
    if (tabId === activeTab) return;
    setActiveTab(tabId);
    setOutput('');
    setError('');
    setPromptData(null);
    setPromptOpen(false);
    setIsEditing(false);
    setEditedText('');
    setOriginalAiText('');
    setSavedEditText('');
    setHasUnsavedEdit(false);
    setTone(DEFAULT_TONE_BY_CONTENT_TYPE[tabId] || 'authoritative');
  };

  const handleGenerate = async () => {
    if (!input.trim()) {
      setError('Please enter some input text before generating.');
      return;
    }
    setLoading(true);
    setError('');

    const isAll = activeTab === 'socialMediaAll';
    const platformLine = isAll
      ? 'Generate one post per platform. Label each section clearly: [YOUTUBE], [FACEBOOK], [INSTAGRAM], [TWITTER/X]'
      : `Number of posts: ${postCount}`;

    const customInstructions = [
      `Hashtags: ${hashtags}`,
      `Emojis: ${emojis}`,
      platformLine,
    ].join('\n');

    try {
      const result = await contentService.generateContent({
        contentType:        activeTab,
        inputContent:       input,
        tone,
        customInstructions,
        language:           'telugu',
      });
      const text = result?.outputs?.primaryOutput || '';
      setOutput(text);
      setOriginalAiText(text);
      setEditedText(text);
      setSavedEditText('');
      setHasUnsavedEdit(false);
      setIsEditing(false);
      setTimeout(() => {
        outputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 80);
    } catch (e) {
      setError(
        e.response?.data?.message ||
        e.message ||
        'Content generation failed. Check your AI provider settings.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!output) return;
    try {
      await navigator.clipboard.writeText(output);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (_) {
      setError('Clipboard access denied by browser.');
    }
  };

  const handleDownload = async (format) => {
    if (!output) return;
    setDownloadOpen(false);
    const tab   = TABS.find((tb) => tb.id === activeTab);
    const tabLabel = tab ? t(tab.labelKey) : 'post';
    const title = `Social Media — ${tabLabel} — ${new Date().toLocaleDateString('en-IN')}`;
    try {
      const blob = await contentService.downloadContent({ text: output, title, format });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `social-${tabLabel.replace(/[\s/]+/g, '-').toLowerCase()}.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (_) {
      setError('Download failed. Please try again.');
    }
  };

  const handleTogglePrompt = async () => {
    if (!promptOpen && !promptData) {
      setPromptLoading(true);
      try {
        const data = await promptService.getPrompt(activeTab);
        setPromptData(data);
      } catch (_) {
        setPromptData(null);
      } finally {
        setPromptLoading(false);
      }
    }
    setPromptOpen((v) => !v);
  };

  const handleClear = () => {
    clearGlobalInputText();
    setOutput('');
    setError('');
    setIsEditing(false);
    setEditedText('');
    setOriginalAiText('');
    setSavedEditText('');
    setHasUnsavedEdit(false);
  };

  // ── Output toolbar handlers ─────────────────────────────────────────────────

  const handleToggleEdit = () => {
    if (!output) return;
    setEditedText(output);
    setIsEditing(true);
  };

  const handleEditableInput = () => {
    const text = editableRef.current?.innerText ?? '';
    setEditedText(text);
    setHasUnsavedEdit(text !== originalAiText);
  };

  const handleDiscardEdit = () => {
    setEditedText(originalAiText);
    setOutput(originalAiText);
    setIsEditing(false);
    setHasUnsavedEdit(false);
  };

  const handleSaveEdit = async () => {
    const finalText = editableRef.current?.innerText ?? editedText;
    setOutput(finalText);
    setEditedText(finalText);
    setSavedEditText(finalText);
    setIsEditing(false);
    setHasUnsavedEdit(false);

    try {
      await contentService.saveEditFeedback({
        contentType: activeTab,
        inputText: input,
        aiOutput: originalAiText,
        userEdit: finalText,
      });
      toast.success('Edit saved — AI will learn from this improvement ✓');
    } catch (e) {
      toast.error(
        e.response?.data?.message ||
        e.message ||
        'Edit saved locally, but learning feedback could not be stored.'
      );
    }
  };

  const isAdmin    = user?.role === 'admin' || user?.role === 'superadmin';
  const isAll      = activeTab === 'socialMediaAll';
  const wc         = (str) => str.trim().split(/\s+/).filter(Boolean).length;

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-4">

      {/* Page header */}
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">{t('socialMedia.title')}</h1>
        <p className="text-sm text-gray-500 mt-0.5">
          {t('socialMedia.desc')}
        </p>
      </div>

      {/* Tab bar — mobile (< 768px): native <select> dropdown.
                   Tablet+ (>= 768px): horizontal tab bar with overflow-x scroll. */}
      <div className="md:hidden">
        <select
          value={activeTab}
          onChange={(e) => handleTabChange(e.target.value)}
          className="input w-full text-sm font-medium"
          aria-label="Select social media platform"
        >
          {TABS.map((tab) => (
            <option key={tab.id} value={tab.id}>{t(tab.labelKey)}</option>
          ))}
        </select>
      </div>
      <div
        className="hidden md:flex border-b border-gray-200 overflow-x-auto"
        style={{ scrollbarWidth: 'none' }}
      >
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => handleTabChange(tab.id)}
            className={`flex-shrink-0 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeTab === tab.id
                ? 'border-primary-500 text-primary-600 bg-primary-50/40'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            {t(tab.labelKey)}
          </button>
        ))}
      </div>

      {/* Controls bar */}
      <div className="flex items-center gap-2 flex-wrap">

        {/* Generate */}
        <button
          onClick={handleGenerate}
          disabled={loading}
          className="btn btn-primary flex items-center gap-2 min-w-[130px] justify-center"
        >
          {loading ? (
            <>
              <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              {t('genpage.generating')}
            </>
          ) : (
            <>
              <FiZap size={14} />
              {t('genpage.generate')}
            </>
          )}
        </button>

        <div className="h-5 w-px bg-gray-200 mx-1" />

        {/* Tone */}
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="text-xs text-gray-500 font-medium whitespace-nowrap">{t('genpage.tone')}</span>
          <ToneSelector
            contentType={activeTab}
            value={tone}
            onChange={setTone}
          />
        </div>

        <div className="h-5 w-px bg-gray-200 mx-1" />

        {/* Hashtags toggle */}
        <div className="flex items-center gap-1.5">
          <FiHash size={13} className="text-gray-400" />
          <SegmentControl
            options={['Include', 'Exclude']}
            labels={[t('label.include'), t('label.exclude')]}
            value={hashtags}
            onChange={setHashtags}
            disabled={loading}
          />
        </div>

        {/* Emojis toggle */}
        <div className="flex items-center gap-1.5">
          <FiSmile size={13} className="text-gray-400" />
          <SegmentControl
            options={['Include', 'Exclude']}
            labels={[t('label.include'), t('label.exclude')]}
            value={emojis}
            onChange={setEmojis}
            disabled={loading}
          />
        </div>

        {/* Post count — hidden for All Platforms */}
        {!isAll && (
          <>
            <div className="h-5 w-px bg-gray-200 mx-1" />
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-gray-500 font-medium whitespace-nowrap">{t('label.posts')}</span>
              <SegmentControl
                options={['1', '3', '5']}
                value={postCount}
                onChange={setPostCount}
                disabled={loading}
              />
            </div>
          </>
        )}

        {/* All Platforms hint badge */}
        {isAll && (
          <span className="text-[10px] font-medium text-primary-600 bg-primary-50 border border-primary-200 rounded-full px-2.5 py-1 whitespace-nowrap">
            {t('socialMedia.postPerPlatform')}
          </span>
        )}

        {/* Right-side actions */}
        <div className="ml-auto flex items-center gap-1.5">

          {/* Copy */}
          <button
            onClick={handleCopy}
            disabled={!output}
            title="Copy to clipboard"
            className="btn btn-ghost flex items-center gap-1.5 text-sm disabled:opacity-40"
          >
            {copySuccess
              ? <FiCheck size={14} className="text-green-500" />
              : <FiCopy size={14} />}
            <span>{copySuccess ? t('genpage.copied') : t('genpage.copy')}</span>
          </button>

          {/* Download dropdown */}
          <div className="relative" ref={downloadRef}>
            <button
              onClick={() => setDownloadOpen((v) => !v)}
              disabled={!output}
              title={t('genpage.download')}
              className="btn btn-ghost flex items-center gap-1.5 text-sm disabled:opacity-40"
            >
              <FiDownload size={14} />
              <span>{t('genpage.download')}</span>
              <FiChevronDown
                size={12}
                className={`text-gray-400 transition-transform ${downloadOpen ? 'rotate-180' : ''}`}
              />
            </button>
            {downloadOpen && (
              <div className="absolute right-0 mt-1 w-40 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden z-20">
                <button
                  onClick={() => handleDownload('docx')}
                  className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition"
                >
                  <FiFileText size={13} className="text-blue-500 flex-shrink-0" />
                  <span>.docx (Word)</span>
                </button>
                <button
                  onClick={() => handleDownload('pdf')}
                  className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition"
                >
                  <FiFile size={13} className="text-red-500 flex-shrink-0" />
                  <span>.pdf</span>
                </button>
              </div>
            )}
          </div>

          {/* Clear */}
          <button
            onClick={handleClear}
            title={t('genpage.clearAll')}
            className="btn btn-ghost flex items-center gap-1.5 text-sm text-gray-500"
          >
            <FiX size={14} />
            <span>{t('genpage.clear')}</span>
          </button>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="flex items-start gap-2 text-sm text-danger-600 bg-danger-50 border border-danger-200 rounded-xl px-3 py-2.5">
          <FiAlertCircle size={14} className="mt-0.5 flex-shrink-0" />
          <span>{error}</span>
          <button onClick={() => setError('')} className="ml-auto text-danger-400 hover:text-danger-600">
            <FiX size={12} />
          </button>
        </div>
      )}

      {/* Two-column editor */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 3xl:max-w-7xl 3xl:mx-auto">

        {/* Input */}
        <div className="flex flex-col gap-1.5">
          <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
            {t('socialMedia.inputLabel')}
          </label>
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={t('genpage.inputPlaceholder')}
            className="input w-full resize-y font-sans text-sm leading-relaxed genpage-textarea mt-[42px]"
          />
          <div className="flex justify-between text-xs text-gray-400">
            <span>{input.length > 0 ? `${input.length} ${t('genpage.characters')}` : ''}</span>
            <span>{input.length > 0 ? `${wc(input)} ${t('genpage.words')}` : ''}</span>
          </div>
        </div>

        {/* Output */}
        <div className="flex flex-col gap-1.5" ref={outputRef}>
          <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
            {isAll
              ? t('socialMedia.generatedAllPlatforms')
              : t('socialMedia.generatedPost', { platform: t(TABS.find(tab => tab.id === activeTab)?.labelKey) })}
          </label>

          <OutputToolbar
            fontSize={fontSize}
            setFontSize={setFontSize}
            fontFamily={fontFamily}
            setFontFamily={setFontFamily}
            isEditing={isEditing}
            onToggleEdit={handleToggleEdit}
            onSaveEdit={handleSaveEdit}
            onDiscardEdit={handleDiscardEdit}
            hasUnsavedEdit={hasUnsavedEdit}
            hasSavedEdit={!!savedEditText}
            originalText={originalAiText}
            editedText={savedEditText || editedText}
          />

          <div className="relative flex-1">
            {isEditing ? (
              <div
                ref={editableRef}
                contentEditable
                suppressContentEditableWarning
                onInput={handleEditableInput}
                className="input w-full resize-y leading-relaxed bg-white text-gray-800 overflow-auto whitespace-pre-wrap focus:outline-none focus:ring-2 focus:ring-primary-500 genpage-textarea"
                style={{ fontFamily, fontSize: `${fontSize}px` }}
              >
                {editedText}
              </div>
            ) : (
              <textarea
                readOnly
                value={output}
                placeholder={
                  loading
                    ? t('socialMedia.aiGenerating')
                    : t('socialMedia.placeholder')
                }
                className="input w-full resize-y leading-relaxed bg-gray-50/80 text-gray-800 output-area genpage-textarea"
                style={{ fontFamily, fontSize: `${fontSize}px` }}
              />
            )}
            {loading && (
              <div className="absolute inset-0 flex items-center justify-center bg-white/60 rounded-xl backdrop-blur-[2px]">
                <div className="flex flex-col items-center gap-2.5 text-primary-600">
                  <div className="w-9 h-9 border-[3px] border-primary-500 border-t-transparent rounded-full animate-spin" />
                  <span className="text-xs font-semibold">{t('socialMedia.aiGenerating')}</span>
                </div>
              </div>
            )}
          </div>
          {output && !loading && (
            <div className="flex justify-between text-xs text-gray-400">
              <span>{output.length} characters</span>
              <span>{wc(output)} words</span>
            </div>
          )}
        </div>
      </div>

      {/* Collapsible "Prompt used" section */}
      <div className="border border-gray-200 rounded-xl overflow-hidden">
        <button
          onClick={handleTogglePrompt}
          className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition text-sm font-medium text-gray-700"
        >
          <span className="flex items-center gap-2">
            <FiCode size={14} className="text-gray-400" />
            {t('genpage.promptUsedFor')}{' '}
            <span className="text-primary-600">
              {t(TABS.find((tab) => tab.id === activeTab)?.labelKey)}
            </span>
          </span>
          <FiChevronDown
            size={14}
            className={`text-gray-400 transition-transform duration-200 ${promptOpen ? 'rotate-180' : ''}`}
          />
        </button>

        {promptOpen && (
          <div className="px-4 py-3 space-y-3 border-t border-gray-200 bg-white">
            {promptLoading ? (
              <p className="text-xs text-gray-400">{t('genpage.loadingPrompt')}</p>
            ) : promptData ? (
              <>
                <div>
                  <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-1.5">
                    {t('genpage.userPromptTemplate')}
                  </p>
                  <pre className="text-xs text-gray-600 bg-gray-50 rounded-lg p-3 overflow-x-auto whitespace-pre-wrap font-mono leading-relaxed border border-gray-100">
                    {promptData.userPromptTemplate || t('genpage.noTemplate')}
                  </pre>
                </div>
                <div className="flex items-start justify-between gap-3">
                  {promptData.notes && (
                    <p className="text-xs text-gray-500 italic flex-1">{promptData.notes}</p>
                  )}
                  {isAdmin && (
                    <button
                      onClick={() => navigate(`/settings/prompt-editor?type=${activeTab}`)}
                      className="flex-shrink-0 flex items-center gap-1 text-xs text-primary-600 hover:underline font-medium"
                    >
                      <FiEdit3 size={11} />
                      {t('genpage.editPrompt')}
                    </button>
                  )}
                </div>
              </>
            ) : (
              <p className="text-xs text-gray-400">
                {t('genpage.promptError')}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
